/*
  Timeline scroll icon.

  Drives a single icon down the rail instead of pinning one to every block. The icon
  tracks a fixed line across the viewport: while the timeline crosses that line the
  icon sits on it, and it parks at whichever end of its travel is nearest once the
  timeline has passed. That is what makes it read as travelling with the page rather
  than jumping between blocks.

  Its travel runs from the middle of the first block to the middle of the last, not
  the full height of the rail — so it starts and ends level with a block the way the
  per-block icons do, rather than at the bar's bare ends.

  The offset is written to a custom property and consumed by a transform, so the work
  stays on the compositor. Reads happen inside a rAF, never in the scroll handler.
*/
class TimelineTrack extends HTMLElement {
   connectedCallback() {
      this.rail = this.querySelector('.timeline__list');
      this.icon = this.querySelector('.timeline__scrollIcon');
      if (!this.rail || !this.icon) return;

      // Share of the viewport height the icon rides at. 0.5 puts it dead centre.
      this.anchorRatio = parseFloat(this.dataset.anchor) || 0.5;
      this.frame = null;
      this.onScroll = this.onScroll.bind(this);

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });

      if ('ResizeObserver' in window) {
         // Images and videos settle after load and change the rail height with them.
         this.observer = new ResizeObserver(this.onScroll);
         this.observer.observe(this.rail);
      }

      this.update();
   }

   disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      this.observer?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
   }

   onScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
         this.frame = null;
         this.update();
      });
   }

   update() {
      const rows = this.rail.querySelectorAll('.timeline__row');
      if (!rows.length) return;

      const railTop = this.rail.getBoundingClientRect().top;
      const first = rows[0].getBoundingClientRect();
      const last = rows[rows.length - 1].getBoundingClientRect();

      // Both ends are measured from the rail's own top, since that is what the icon's
      // offset is relative to. With a single block the two collapse and the icon simply
      // holds that one position.
      const start = first.top - railTop + first.height / 2;
      const end = last.top - railTop + last.height / 2;

      const anchor = window.innerHeight * this.anchorRatio;
      const offset = Math.min(Math.max(anchor - railTop, start), end);
      this.style.setProperty('--timeline-icon-offset', `${offset}px`);
   }
}

customElements.get('timeline-track') || customElements.define('timeline-track', TimelineTrack);
